import React, { useMemo, useState } from "react";
import {
    DataGridPremium,
    useGridApiRef,
    gridSortedRowIdsSelector,
} from "@mui/x-data-grid-premium";
import { useNewItemForecastContext } from "../Contexts/newItemForecastContext";
import dayjs from "dayjs";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, LinearProgress, Stack, Tooltip, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { isInt } from "../Utilities/utilities.js";
import { Alert } from "@mui/lab";
import { usePrompt } from "../Utilities/usePrompt.ts";
import { useProductsContext } from "../Contexts/productsContext";
import { useDistributorsContext } from "../Contexts/distributorsContext.jsx";
import { useSKUFulfillmentTypesContext } from "../Contexts/skuFulfillmentTypesContext";
import PropTypes from 'prop-types';

const WEEK_COUNT = 9;
const INPUT_MULTIPLIER_DIFFERENCE_FACTOR = 10;

const isForecastAmountInputValid = (adjustedAmount, originalAmount) => (
    adjustedAmount <= originalAmount * INPUT_MULTIPLIER_DIFFERENCE_FACTOR &&
    adjustedAmount >= originalAmount / INPUT_MULTIPLIER_DIFFERENCE_FACTOR
);

const weekEndDateForIndex = (inMarketDate, index) =>
    dayjs(inMarketDate).add(index, "week").endOf("week");

const matchForecast = (row, weekEnd) =>
    row.forecastAmounts?.find(fa => dayjs(fa.weekEndDate).endOf("week").isSame(weekEnd, "day"));

const InMarketDateGroupGrid = ({ inMarketDate, items, isReadOnly, loading, onEdited }) => {
    const apiRef = useGridApiRef();
    const { skuFulfillmentTypes } = useSKUFulfillmentTypesContext();

    const weekEndDates = useMemo(() => (
        Array.from({ length: WEEK_COUNT }, (_, i) => weekEndDateForIndex(inMarketDate, i))
    ), [inMarketDate]);

    const fulfillmentMethodById = useMemo(() => {
        const map = new Map();
        (skuFulfillmentTypes ?? []).forEach(t => map.set(t.id, t.description));
        return map;
    }, [skuFulfillmentTypes]);

    const handleCellKeyDown = (params, event) => {
        if (event.key === "Tab") {
            event.preventDefault();
            event.defaultMuiPrevented = true;

            const allRowIds = gridSortedRowIdsSelector(apiRef);
            const currentRowIndex = allRowIds.indexOf(params.id);
            const isShift = event.shiftKey;

            const editableColumns = apiRef.current
                .getAllColumns()
                .filter((col) => col.editable)
                .map((col) => col.field);

            if (editableColumns.length === 0) return;

            const currentColIndex = editableColumns.indexOf(params.field);
            let nextRowIndex = currentRowIndex;
            let nextColIndex = isShift ? currentColIndex - 1 : currentColIndex + 1;

            if (nextColIndex >= editableColumns.length) {
                nextColIndex = 0;
                nextRowIndex = currentRowIndex + 1;
            } else if (nextColIndex < 0) {
                nextColIndex = editableColumns.length - 1;
                nextRowIndex = currentRowIndex - 1;
            }

            if (nextRowIndex >= 0 && nextRowIndex < allRowIds.length) {
                const nextRowId = allRowIds[nextRowIndex];
                const nextField = editableColumns[nextColIndex];
                apiRef.current.stopCellEditMode({ id: params.id, field: params.field });
                setTimeout(() => {
                    apiRef.current.scrollToIndexes({
                        rowIndex: nextRowIndex,
                        colIndex: apiRef.current.getAllColumns().findIndex((col) => col.field === nextField),
                    });
                    apiRef.current.setCellFocus(nextRowId, nextField);
                    apiRef.current.startCellEditMode({ id: nextRowId, field: nextField });
                }, 50);
            }
        }
    };

    const columnDefinitions = useMemo(() => {
        const baseColumns = [{
            field: "distributorSapId",
            headerName: "Distributor",
            headerAlign: "center",
            width: 90,
            valueGetter: (params) => params.row.distributorSapId,
        }, {
            field: "product.sku",
            headerName: "BBC",
            headerAlign: "center",
            width: 80,
            valueGetter: (params) => params.row.product?.sku,
        }, {
            field: "customerPartNumber",
            headerName: "Customer",
            headerAlign: "center",
            width: 90,
            valueGetter: (params) => params.row.customerPartNumber,
        }, {
            field: "fulfillmentMethod",
            headerName: "Fulfillment Method",
            headerAlign: "center",
            width: 130,
            valueGetter: (params) => fulfillmentMethodById.get(params.row.fulfillmentMethodId) ?? "",
        }, {
            field: "inMarketDate",
            headerName: "In-Market Date",
            headerAlign: "center",
            width: 110,
            valueGetter: (params) => {
                const date = params.row.inMarketDate;
                if (!date) return "";
                return dayjs(date).format("MM/DD/YY");
            },
        }];

        for (let i = 0; i < WEEK_COUNT; i++) {
            const weekEnd = weekEndDates[i];
            const weekLabel = weekEnd.format("MM/DD/YY");

            baseColumns.push({
                field: `forecastAmount[${i}].originalAmount`,
                headerName: "F",
                width: 60,
                type: "number",
                headerAlign: "center",
                align: "right",
                valueGetter: (params) => {
                    const found = matchForecast(params.row, weekEnd);
                    if (!found || found.originalAmount == null || isNaN(found.originalAmount)) return "";
                    return Math.round(found.originalAmount);
                },
            });
            baseColumns.push({
                field: `forecastAmount[${i}].adjustedAmount`,
                headerName: "A",
                width: 75,
                editable: !isReadOnly,
                type: "number",
                headerAlign: "center",
                renderCell: (params) => (
                    <Tooltip title={weekLabel} placement="top" arrow>
                        <span style={{ width: '100%', textAlign: 'right' }}>
                            {params.formattedValue}
                        </span>
                    </Tooltip>
                ),
                valueGetter: (params) => {
                    const found = matchForecast(params.row, weekEnd);
                    if (!found || found.forecastAdjustmentId === 0 || found.adjustedAmount == null || isNaN(found.adjustedAmount)) return "";
                    return Math.round(found.adjustedAmount);
                },
                valueSetter: (params) => {
                    const found = matchForecast(params.row, weekEnd);
                    const parsed = parseInt(params.value);
                    const valid = isInt(parsed) && parsed >= 0;

                    if (valid) {
                        if (found) {
                            found.adjustedAmount = parsed;
                        } else {
                            params.row.forecastAmounts.push({
                                weekEndDate: weekEnd.toDate(),
                                weekId: weekEnd.valueOf(),
                                adjustedAmount: parsed,
                                originalAmount: null,
                                forecastAdjustmentId: null,
                            });
                        }
                    } else if (found) {
                        found.adjustedAmount = null;
                    } else {
                        params.row.forecastAmounts.push({
                            weekEndDate: weekEnd.toDate(),
                            weekId: weekEnd.valueOf(),
                            adjustedAmount: null,
                            originalAmount: null,
                            forecastAdjustmentId: null,
                        });
                    }
                    return params.row;
                },
            });
        }

        return baseColumns;
    }, [weekEndDates, isReadOnly, fulfillmentMethodById]);

    const columnGroupingModel = useMemo(() => {
        const forecastsGroup = {
            groupId: "forecasts",
            description: "Forecast",
            headerAlign: "left",
            renderHeaderGroup: () => (
                <div className="MuiDataGrid-columnHeaderTitle css-t89xny-MuiDataGrid-columnHeaderTitle">
                    <Stack direction="horizontal" alignItems="center">
                        <Typography variant="h5">
                            <strong>(F) Depletions Forecast &amp; (A) Customer Adjustments (Units)</strong>
                        </Typography>
                    </Stack>
                </div>
            ),
            children: [],
        };

        for (let i = 0; i < WEEK_COUNT; i++) {
            const label = weekEndDates[i].format("MM/DD/YY");
            forecastsGroup.children.push({
                groupId: `Week ${i + 1}`,
                headerAlign: "center",
                renderHeaderGroup: () => (
                    <span>{label}</span>
                ),
                children: [
                    { field: `forecastAmount[${i}].originalAmount` },
                    { field: `forecastAmount[${i}].adjustedAmount` },
                ],
            });
        }

        return [{
            groupId: "SKU",
            description: "SKU",
            headerAlign: "center",
            children: [{ field: "product.sku" }, { field: "customerPartNumber" }],
        }, forecastsGroup];
    }, [weekEndDates]);

    return (
        <Accordion defaultExpanded disableGutters sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: "#f5f5f5" }}>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    In-Market Date: {inMarketDate ? dayjs(inMarketDate).format("MM/DD/YYYY") : "—"} ({items.length} {items.length === 1 ? "item" : "items"})
                </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
                <DataGridPremium
                autoHeight
                hideFooter
                rows={items}
                columns={columnDefinitions}
                apiRef={apiRef}
                columnGroupingModel={columnGroupingModel}
                loading={loading}
                disableColumnMenu
                rowHeight={30}
                columnHeaderHeight={30}
                experimentalFeatures={{ columnGrouping: true, newEditingApi: true }}
                isCellEditable={(params) => {
                    if (isReadOnly) return false;
                    const match = params.field.match(/\[(\d+)\]/);
                    if (!match) return false;
                    const idx = parseInt(match[1], 10);
                    const found = matchForecast(params.row, weekEndDates[idx]);
                    return found && found.originalAmount != null && !isNaN(parseInt(found.originalAmount));
                }}
                onCellEditStart={(_params, event) => {
                    if (event.type != "dblclick") {
                        event.preventDefault();
                        event.defaultMuiPrevented = true;
                    }
                }}
                onCellKeyDown={handleCellKeyDown}
                onCellEditStop={(params) => {
                    setTimeout(() => {
                        const match = params.field.match(/\[(\d+)\]/);
                        if (!match) return;
                        const idx = parseInt(match[1], 10);
                        const found = matchForecast(params.row, weekEndDates[idx]);
                        if (found && found.originalAmount != null && found.adjustedAmount != null &&
                            !isForecastAmountInputValid(found.adjustedAmount, found.originalAmount)) {
                            alert("The adjustment made is +/- 10x than the current forecast.  Please verify this adjustment is correct.");
                        }
                    }, 150);
                    onEdited();
                }}
                slotProps={{
                    cell: {
                        onFocus: event => {
                            if (event.target?.tagName.toLowerCase() === "input") {
                                event.target?.select();
                            }
                        },
                    },
                }}
                getCellClassName={(params) => (
                    params.field.includes("adjusted")
                        ? (isInt(params.value) || params.value === " ") ? "adjustable" : "adjusted"
                        : " "
                )}
                />
            </AccordionDetails>
        </Accordion>
    );
};

InMarketDateGroupGrid.propTypes = {
    inMarketDate: PropTypes.any,
    items: PropTypes.array.isRequired,
    isReadOnly: PropTypes.bool,
    loading: PropTypes.bool,
    onEdited: PropTypes.func.isRequired,
};

export const NewItemForecastGrid = () => {
    const {
        forecastReviewItems,
        SaveForecastAdjustmentChanges,
        isReadOnly,
        error,
    } = useNewItemForecastContext();
    const { setSelectedProducts } = useProductsContext();
    const { selectedDistributors } = useDistributorsContext();
    const [edited, setEdited] = useState(false);
    const [sending, setSending] = useState(false);

    usePrompt("You have unsaved changes. Are you sure you want to leave?", edited);

    const groups = useMemo(() => {
        const map = new Map();
        forecastReviewItems.forEach(item => {
            const key = item.inMarketDate
                ? dayjs(item.inMarketDate).startOf("day").format("YYYY-MM-DD")
                : "no-date";
            if (!map.has(key)) map.set(key, { key, inMarketDate: item.inMarketDate, items: [] });
            map.get(key).items.push(item);
        });

        return Array.from(map.values()).sort((a, b) => {
            if (!a.inMarketDate) return 1;
            if (!b.inMarketDate) return -1;
            return dayjs(a.inMarketDate).valueOf() - dayjs(b.inMarketDate).valueOf();
        });
    }, [forecastReviewItems]);

    return (<div>
        {isReadOnly ? (
            <Stack direction="row" alignItems="center" justifyContent="center">
                <Alert variant="filled" severity="info" sx={{ alignItems: 'center' }}>
                    {selectedDistributors.length > 1
                        ? <Typography variant="h6">One of the selected distributors uses an automated system for forecast adjustment. This view is read only.</Typography>
                        : <Typography variant="h6">The selected distributor uses an automated system for forecast adjustment. This view is read only.</Typography>}
                </Alert>
            </Stack>
        ) : null}

        {error ? (
            <Alert variant="filled" severity="error" action={
                <Button color="inherit" size="small" onClick={() => window.location.reload()}>Refresh Page</Button>
            }>
                Updates have failed! Refresh the page before trying again.
            </Alert>
        ) : (<Box sx={{
            height: "calc(100vh - 280px)", width: "100%", padding: 2,
            display: "flex", flexDirection: "column",
            "& .adjustable": { backgroundColor: "#d2f7d4", color: "#000000" },
            "& .adjusted": { backgroundColor: "#c1f7ec", color: "#000000" },
            "& .banded": { backgroundColor: "#a9dbfc", color: "#000000" },
            "& .locked": { backgroundColor: "#EBEBE4", color: "#000000" },
        }}>
            <Alert style={{ marginBottom: 8 }} severity="warning">
                Note that the forecasted quantity displayed is representative of the forecast for both direct and any partnered indirect wholesalers. Please ensure any forecast adjustments also account for indirect wholesalers.
            </Alert>

            <Box sx={{ flex: 1, overflowY: "auto", pr: 1 }}>
                {groups.map(group => (
                    <InMarketDateGroupGrid
                        key={group.key}
                        inMarketDate={group.inMarketDate}
                        items={group.items}
                        isReadOnly={isReadOnly}
                        loading={sending}
                        onEdited={() => setEdited(true)}
                    />
                ))}
            </Box>

            <Box sx={{
                borderTop: "1px solid rgba(0, 0, 0, 0.12)",
                backgroundColor: "background.paper",
                pt: 2, mt: 1,
            }}>
                <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={2}>
                    <Button
                        variant="contained"
                        onClick={async () => {
                            setSending(true);
                            await SaveForecastAdjustmentChanges();
                            setSending(false);
                            setEdited(false);
                            setSelectedProducts([]);
                        }}
                        disabled={!edited}
                    >
                        Submit
                    </Button>
                    <Typography variant="body">
                        I acknowledge and agree that BBC will generate shipments based upon reported wholesaler inventory and forecasted depletions, subject to change pending transportation and product availability.
                    </Typography>
                    {sending && <LinearProgress size={24} />}
                </Stack>
            </Box>
        </Box>)}
    </div>);
};

NewItemForecastGrid.propTypes = {};
